import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeGranularity } from "@@/shared/date-time/time-granularity";
import { timeGranularityZodSchema } from "@@/shared/date-time/time-granularity";
import type { Price } from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ResourcePriceId } from "./resource-price-id";
import {
    resourcePriceIdFactory,
    resourcePriceIdZodSchema,
} from "./resource-price-id";

type ResourcePriceByTime = {
    readonly _id: ResourcePriceId;
    readonly _type: "RESOURCE_PRICE_PRICE_BY_TIME";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly price: Price;
    readonly step: number;
    readonly granularity: TimeGranularity;
};

type resourcePriceByTimeFactoryParams = Except<
    ResourcePriceByTime,
    "_id" | "_type" | "_createdAt"
> & { _id?: ResourcePriceId };

const resourcePriceByTimeFactory = (
    params: resourcePriceByTimeFactoryParams,
): ResourcePriceByTime => {
    return {
        ...params,
        _id: params._id || resourcePriceIdFactory(),
        _type: "RESOURCE_PRICE_PRICE_BY_TIME",
        _createdAt: nowIsoAndUnix(),
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isResourcePriceByTime = (item: any): item is ResourcePriceByTime => {
    return item._type === "RESOURCE_PRICE_PRICE_BY_TIME";
};

const resourcePriceByTimeZodSchema: z.ZodSchema<ResourcePriceByTime> = z.object(
    {
        _id: resourcePriceIdZodSchema,
        _type: z.literal("RESOURCE_PRICE_PRICE_BY_TIME"),
        _createdAt: isoAndUnixZodSchema,
        price: priceZodSchema,
        step: z.number(),
        granularity: timeGranularityZodSchema,
    },
);

export {
    isResourcePriceByTime,
    resourcePriceByTimeFactory,
    resourcePriceByTimeZodSchema,
};
export type { ResourcePriceByTime };
